import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { userId, type UserId } from './User.js'

/** Identifier type for public.challenge_ */
export type ChallengeId = number & { __brand: 'ChallengeId' }

/** Represents the table public.challenge_ */
export default interface ChallengeTable {
  /** Database type: pg_catalog.int4 */
  id: ColumnType<ChallengeId, never, never>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.numeric */
  geoLat: ColumnType<number, number, number>

  /** Database type: pg_catalog.numeric */
  geoLon: ColumnType<number, number, number>

  /** Database type: pg_catalog.text */
  description: ColumnType<string, string, string>

  /** Database type: pg_catalog.bool */
  isReviewed: ColumnType<boolean | null, boolean | null, boolean | null>
}

export type Challenge = Selectable<ChallengeTable>

export type ChallengeInitializer = Insertable<ChallengeTable>

export type ChallengeMutator = Updateable<ChallengeTable>

export const challengeId = z.number() as unknown as z.Schema<ChallengeId>

export const challenge = z.object({
  id: challengeId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  geoLat: z.number(),
  geoLon: z.number(),
  description: z.string(),
  isReviewed: z.boolean().nullable().optional(),
}) as unknown as z.Schema<Challenge>

export const challengeInitializer = z.object({
  id: challengeId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  geoLat: z.number(),
  geoLon: z.number(),
  description: z.string(),
  isReviewed: z.boolean().optional().nullable(),
}) as unknown as z.Schema<ChallengeInitializer>

export const challengeMutator = z.object({
  id: challengeId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  geoLat: z.number().optional(),
  geoLon: z.number().optional(),
  description: z.string().optional(),
  isReviewed: z.boolean().optional().nullable(),
}) as unknown as z.Schema<ChallengeMutator>
